import React, { useState, useEffect } from 'react';
import { useFechasEspeciales } from '@/hooks/useFechasEspeciales';
import * as festivosApiService from '@/services/festivosApiService';
import * as fechasEspecialesApiService from '@/services/fechasEspecialesApiService';
import * as horariosBloqueadosApiService from '@/services/horariosBloqueadosApiService';
import { useSucursal } from '@/context/SucursalContext';
import toastService from '@/utils/toastService';
import Modal from '@/components/common/Modal';
import CustomSelect from '@/components/common/CustomSelect';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import { HorarioSelectorCompact } from '@/components/common/HorarioSelectorCompact';
import { HorarioEspecialDia } from '@/components/common/HorarioEspecialDia';
import { FaCalendar, FaClock, FaMapMarkerAlt, FaFileAlt, FaTags, FaSave, FaPen, FaTrash } from 'react-icons/fa';
import moment from 'moment';
import 'moment/locale/es';
import ValidatedInput from '@/components/common/ValidatedInput';

moment.locale('es');

// Función para formatear horarios especiales
const formatearHorarioEspecial = (horarioEspecial) => {
  if (!horarioEspecial) return null;
  
  try {
    const horario = typeof horarioEspecial === 'string' ? JSON.parse(horarioEspecial) : horarioEspecial;
    const horarios = [];
    
    // Verificar turno mañana (con ñ)
    if (horario.mañana && horario.mañana.activo) {
      horarios.push(`${horario.mañana.inicio}-${horario.mañana.fin}`);
    }
    
    // Verificar turno tarde
    if (horario.tarde && horario.tarde.activo) {
      horarios.push(`${horario.tarde.inicio}-${horario.tarde.fin}`);
    }
    
    return horarios.length > 0 ? horarios.join(', ') : null;
  } catch (error) {
    console.error('Error al formatear horario especial:', error);
    return null;
  }
};

// Función para convertir códigos de región a nombres completos
const getRegionName = (codigo) => {
  const regiones = {
    'XV': 'Arica y Parinacota',
    'I': 'Tarapacá',
    'II': 'Antofagasta', 
    'III': 'Atacama',
    'IV': 'Coquimbo',
    'V': 'Valparaíso',
    'RM': 'Región Metropolitana',
    'VI': 'O\'Higgins',
    'VII': 'Maule',
    'XVI': 'Ñuble',
    'VIII': 'Biobío',
    'IX': 'La Araucanía',
    'XIV': 'Los Ríos',
    'X': 'Los Lagos',
    'XI': 'Aysén',
    'XII': 'Magallanes',
    'VAL': 'Valparaíso',
    'TA': 'Tarapacá',
    'AN': 'Antofagasta',
    'AT': 'Atacama',
    'CO': 'Coquimbo',
    'VS': 'Valparaíso',
    'LI': 'O\'Higgins',
    'ML': 'Maule',
    'NB': 'Ñuble',
    'BI': 'Biobío',
    'AR': 'La Araucanía',
    'LR': 'Los Ríos',
    'LL': 'Los Lagos',
    'AI': 'Aysén',
    'MA': 'Magallanes'
  };
  return regiones[codigo] || codigo;
};

/**
 * Componente para gestionar festivos y fechas especiales de la sucursal
 */
const GestionFestivos = () => {
  const { selectedSucursal, sucursalData } = useSucursal();
  
  // Estados principales
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('festivos');
  
  // Estados para festivos
  const [festivosNacionales, setFestivosNacionales] = useState([]);
  const [festivosSucursal, setFestivosSucursal] = useState([]);
  const [politicaFestivos, setPoliticaFestivos] = useState('configuracion_individual');
  
  // Estados para fechas especiales
  const [fechasEspeciales, setFechasEspeciales] = useState([]);
  const [showFechaEspecialModal, setShowFechaEspecialModal] = useState(false);
  const [editingFechaEspecial, setEditingFechaEspecial] = useState(null);
  
  // Estados para horarios bloqueados
  const [horariosBloqueados, setHorariosBloqueados] = useState([]);
  const [showHorarioBloqueadoModal, setShowHorarioBloqueadoModal] = useState(false);
  const [editingHorarioBloqueado, setEditingHorarioBloqueado] = useState(null);

  // Estados para modales
  const [showHorarioEspecialModal, setShowHorarioEspecialModal] = useState(false);
  const [festivoParaHorario, setFestivoParaHorario] = useState(null);
  const [horarioEspecial, setHorarioEspecial] = useState(null);

  // Formularios
  const [formFechaEspecial, setFormFechaEspecial] = useState({
    nombre: '',
    tipo: 'cierre_total',
    fecha_inicio: '',
    fecha_fin: '',
    motivo: '',
    horario_especial: null
  });

  const [formHorarioBloqueado, setFormHorarioBloqueado] = useState({
    dia_semana: 'lunes',
    hora_inicio: '12:00',
    hora_fin: '13:00',
    motivo: '',
    fecha_inicio_vigencia: moment().format('YYYY-MM-DD'),
    fecha_fin_vigencia: ''
  });

  // Cargar datos iniciales
  useEffect(() => {
    if (selectedSucursal) {
      cargarDatos();
    }
  }, [selectedSucursal]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      // Cargar festivos nacionales para el año actual
      const festivosNac = await festivosApiService.getFestivosByYear(moment().year());
      
      // Cargar datos usando los nuevos servicios
      const [configuracionFestivos, fechasEspecialesData, horariosBloqueadosData] = await Promise.all([
        festivosApiService.getConfiguracionFestivosSucursal(selectedSucursal),
        fechasEspecialesApiService.getFechasEspecialesBySucursal(selectedSucursal),
        horariosBloqueadosApiService.getHorariosBloqueadosBySucursal(selectedSucursal)
      ]);

      setFestivosNacionales(festivosNac);
      setFestivosSucursal(configuracionFestivos.festivos || []);
      setFechasEspeciales(fechasEspecialesData || []);
      setHorariosBloqueados(horariosBloqueadosData || []);
      
      // Obtener política de festivos desde la configuración
      setPoliticaFestivos(configuracionFestivos.politica || 'configuracion_individual');
      
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toastService.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambio de política de festivos
  const handlePoliticaChange = async (nuevaPolitica) => {
    try {
      await festivosApiService.actualizarPoliticaFestivos(selectedSucursal, nuevaPolitica);
      setPoliticaFestivos(nuevaPolitica);
      toastService.success('Política de festivos actualizada');
    } catch (error) {
      console.error('Error al actualizar política:', error);
      toastService.error('Error al actualizar la política');
    }
  };

  // Manejar configuración de festivo
  const handleFestivoConfig = async (festivoId, comportamiento, horarioEsp = null) => {
    try {
      if (comportamiento === 'horario_especial' && !horarioEsp) {
        // Abrir modal para configurar horario especial
        const festivo = festivosNacionales.find(f => f.id === festivoId);
        setFestivoParaHorario(festivo);
        setShowHorarioEspecialModal(true);
        return;
      }

      await festivosApiService.configurarFestivoSucursal(selectedSucursal, festivoId, {
        comportamiento,
        horario_especial: horarioEsp
      });

      await cargarDatos();
      toastService.success('Configuración de festivo actualizada');
    } catch (error) {
      console.error('Error al configurar festivo:', error);
      toastService.error('Error al configurar el festivo');
    }
  };

  // Guardar horario especial para festivo
  const handleGuardarHorarioEspecial = async () => {
    if (!festivoParaHorario || !horarioEspecial) return;

    try {
      await festivosApiService.configurarFestivoSucursal(selectedSucursal, festivoParaHorario.id, {
        comportamiento: 'horario_especial',
        horario_especial: horarioEspecial
      });

      await cargarDatos();
      setShowHorarioEspecialModal(false);
      setFestivoParaHorario(null);
      setHorarioEspecial(null);
      toastService.success('Horario especial configurado');
    } catch (error) {
      console.error('Error al guardar horario especial:', error);
      toastService.error('Error al guardar el horario especial');
    }
  };

  // Obtener configuración actual de un festivo
  const getFestivoConfig = (festivoId) => {
    const config = festivosSucursal.find(fs => fs.festivo_id === festivoId);
    return config ? config.comportamiento : 'horario_normal';
  };

  // Obtener horario especial de un festivo
  const getFestivoHorarioEspecial = (festivoId) => {
    const config = festivosSucursal.find(fs => fs.festivo_id === festivoId);
    return config && config.comportamiento === 'horario_especial' ? config.horario_especial : null;
  };

  // Manejar fecha especial
  const handleFechaEspecial = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formFechaEspecial,
        sucursal_id: selectedSucursal,
        fecha_fin: formFechaEspecial.fecha_fin || null,
        horario_especial: formFechaEspecial.tipo === 'horario_especial' ? 
          formFechaEspecial.horario_especial : null
      };

      if (editingFechaEspecial) {
        await fechasEspecialesApiService.updateFechaEspecial(editingFechaEspecial.id, data);
        toastService.success('Fecha especial actualizada');
      } else {
        await fechasEspecialesApiService.createFechaEspecial(data);
        toastService.success('Fecha especial creada');
      }

      await cargarDatos();
      setShowFechaEspecialModal(false);
      resetFormFechaEspecial();
    } catch (error) {
      console.error('Error al guardar fecha especial:', error);
      toastService.error('Error al guardar la fecha especial');
    }
  };

  // Manejar horario bloqueado
  const handleHorarioBloqueado = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formHorarioBloqueado,
        sucursal_id: selectedSucursal,
        fecha_fin_vigencia: formHorarioBloqueado.fecha_fin_vigencia || null
      };

      if (editingHorarioBloqueado) {
        await horariosBloqueadosApiService.updateHorarioBloqueado(editingHorarioBloqueado.id, data);
        toastService.success('Horario bloqueado actualizado');
      } else {
        await horariosBloqueadosApiService.createHorarioBloqueado(data);
        toastService.success('Horario bloqueado creado');
      }

      await cargarDatos();
      setShowHorarioBloqueadoModal(false);
      resetFormHorarioBloqueado();
    } catch (error) {
      console.error('Error al guardar horario bloqueado:', error);
      toastService.error('Error al guardar el horario bloqueado');
    }
  };

  // Funciones de reset
  const resetFormFechaEspecial = () => {
    setFormFechaEspecial({
      nombre: '',
      tipo: 'cierre_total',
      fecha_inicio: '',
      fecha_fin: '',
      motivo: '',
      horario_especial: null
    });
    setEditingFechaEspecial(null);
  };

  const resetFormHorarioBloqueado = () => {
    setFormHorarioBloqueado({
      dia_semana: 'lunes',
      hora_inicio: '12:00',
      hora_fin: '13:00',
      motivo: '',
      fecha_inicio_vigencia: moment().format('YYYY-MM-DD'),
      fecha_fin_vigencia: ''
    });
    setEditingHorarioBloqueado(null);
  };

  // Funciones de edición
  const editFechaEspecial = (fechaEsp) => {
    setFormFechaEspecial({
      nombre: fechaEsp.nombre,
      tipo: fechaEsp.tipo,
      fecha_inicio: fechaEsp.fecha_inicio,
      fecha_fin: fechaEsp.fecha_fin || '',
      motivo: fechaEsp.motivo || '',
      horario_especial: fechaEsp.horario_especial
    });
    setEditingFechaEspecial(fechaEsp);
    setShowFechaEspecialModal(true);
  };

  const editHorarioBloqueado = (horario) => {
    setFormHorarioBloqueado({
      dia_semana: horario.dia_semana,
      hora_inicio: horario.hora_inicio,
      hora_fin: horario.hora_fin,
      motivo: horario.motivo,
      fecha_inicio_vigencia: horario.fecha_inicio_vigencia,
      fecha_fin_vigencia: horario.fecha_fin_vigencia || ''
    });
    setEditingHorarioBloqueado(horario);
    setShowHorarioBloqueadoModal(true);
  };

  // Funciones de eliminación
  const deleteFechaEspecial = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta fecha especial?')) return;
    
    try {
      await fechasEspecialesApiService.deleteFechaEspecial(id);
      await cargarDatos();
      toastService.success('Fecha especial eliminada');
    } catch (error) {
      console.error('Error al eliminar fecha especial:', error);
      toastService.error('Error al eliminar la fecha especial');
    }
  };

  const deleteHorarioBloqueado = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este horario bloqueado?')) return;
    
    try {
      await horariosBloqueadosApiService.deleteHorarioBloqueado(id);
      await cargarDatos();
      toastService.success('Horario bloqueado eliminado');
    } catch (error) {
      console.error('Error al eliminar horario bloqueado:', error);
      toastService.error('Error al eliminar el horario bloqueado');
    }
  };

  if (!selectedSucursal) {
    return (
      <div className="text-center text-gray-400 py-8">
        Selecciona una sucursal para gestionar festivos y fechas especiales
      </div>
    );
  }

  // Mostrar loading si los datos están cargando
  if (loading) {
    return (
      <div className="flex items-center justify-center bg-gray-900 py-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Simple Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">Gestión de Horarios Especiales</h1>
        <p className="text-gray-400 text-sm">
          Configura festivos, fechas especiales y horarios bloqueados para {sucursalData?.nombre}
        </p>
      </div>

      {/* Modern Segmented Control Tabs */}
      <div className="mb-6">
        <div className="relative bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-1.5">
          <div className="flex gap-2 justify-center items-center md:justify-start">
            {[
              { id: 'festivos', label: 'Festivos', icon: FaCalendar },
              { id: 'fechas', label: 'Fechas Especiales', icon: FaClock },
              { id: 'horarios', label: 'Horarios Bloqueados', icon: FaMapMarkerAlt }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg shadow-blue-500/25 transform scale-[1.02]'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <tab.icon className="text-base" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                {activeTab === tab.id && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-500/20 rounded-xl"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

          {/* Tab Content */}
      {activeTab === 'festivos' && (
        <div className="space-y-6">
          {/* Modern Política general Card */}
          <div className="relative bg-gray-800/60 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6 shadow-xl">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-t-2xl"></div>
            
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-gradient-to-r from-blue-500/20 to-purple-600/20 rounded-xl">
                <FaFileAlt className="text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Política General de Festivos</h3>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <label className="group relative flex items-center p-3 rounded-lg border border-gray-700/50 cursor-pointer transition-all hover:border-blue-500/50 hover:bg-blue-500/5">
                <input
                  type="radio"
                  name="politica"
                  value="bloquear_todos"
                  checked={politicaFestivos === 'bloquear_todos'}
                  onChange={(e) => handlePoliticaChange(e.target.value)}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500 focus:ring-2 flex-shrink-0"
                />
                <div className="ml-3 flex-1">
                  <span className="text-white font-medium text-sm block">Cerrar automáticamente todos los festivos</span>
                  <p className="text-gray-400 text-xs mt-0.5">Todos los festivos nacionales cerrarán el taller automáticamente</p>
                </div>
                {politicaFestivos === 'bloquear_todos' && (
                  <div className="absolute right-3 top-3 p-0.5 bg-green-500/20 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  </div>
                )}
              </label>
              
              <label className="group relative flex items-center p-3 rounded-lg border border-gray-700/50 cursor-pointer transition-all hover:border-purple-500/50 hover:bg-purple-500/5">
                <input
                  type="radio"
                  name="politica"
                  value="configuracion_individual"
                  checked={politicaFestivos === 'configuracion_individual'}
                  onChange={(e) => handlePoliticaChange(e.target.value)}
                  className="w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 focus:ring-purple-500 focus:ring-2 flex-shrink-0"
                />
                <div className="ml-3 flex-1">
                  <span className="text-white font-medium text-sm block">Configurar cada festivo individualmente</span>
                  <p className="text-gray-400 text-xs mt-0.5">Personaliza el comportamiento de cada festivo por separado</p>
                </div>
                {politicaFestivos === 'configuracion_individual' && (
                  <div className="absolute right-3 top-3 p-0.5 bg-green-500/20 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Modern Lista de festivos */}
          {politicaFestivos === 'configuracion_individual' && (
            <div className="relative bg-gray-800/60 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6 shadow-xl">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-t-2xl"></div>
              
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-gradient-to-r from-purple-500/20 to-indigo-600/20 rounded-xl">
                  <FaCalendar className="text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white">Configuración de Festivos</h3>
              </div>
              {/* Vista móvil: Lista vertical */}
              <div className="block md:hidden space-y-3">
                {festivosNacionales.map(festivo => {
                  const config = getFestivoConfig(festivo.id);
                  const fecha = moment(festivo.fecha);
                  
                  return (
                    <div key={festivo.id} className="py-4 border-b border-gray-700 last:border-b-0">
                      <div className="flex flex-col gap-3">
                        <div className="flex-1">
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-white">{festivo.nombre}</span>
                              {config === 'horario_especial' && getFestivoHorarioEspecial(festivo.id) && (
                                <Badge variant="warning" size="sm">
                                  {formatearHorarioEspecial(getFestivoHorarioEspecial(festivo.id))}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-400">
                                {fecha.format('DD [de] MMMM YYYY')}
                              </span>
                              {festivo.tipo === 'regional' && (
                                <Badge variant="primary" size="sm">
                                  {getRegionName(festivo.region_codigo)}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="w-full">
                          <CustomSelect
                            value={config}
                            onChange={(e) => handleFestivoConfig(festivo.id, e.target.value)}
                            options={[
                              { id: 'horario_normal', value: 'horario_normal', label: 'Horario Normal' },
                              { id: 'horario_especial', value: 'horario_especial', label: 'Horario Especial' },
                              { id: 'cerrado', value: 'cerrado', label: 'Cerrado' }
                            ]}
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Vista escritorio: Cards en grid de 3 columnas */}
              <div className="hidden md:grid md:grid-cols-3 md:gap-3">
                {festivosNacionales.map(festivo => {
                  const config = getFestivoConfig(festivo.id);
                  const fecha = moment(festivo.fecha);
                  
                  return (
                    <div key={festivo.id} className="bg-gray-700 p-4 rounded-lg border border-gray-600 hover:border-gray-500 transition-colors">
                      <div className="flex flex-col h-full">
                        <div className="flex-1 mb-4">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-white leading-tight pr-2">
                              {festivo.nombre}
                            </h4>
                            {config === 'horario_especial' && getFestivoHorarioEspecial(festivo.id) && (
                              <Badge variant="warning" size="sm" className="flex-shrink-0">
                                {formatearHorarioEspecial(getFestivoHorarioEspecial(festivo.id))}
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <span className="text-sm text-gray-400">
                              {fecha.format('DD [de] MMMM YYYY')}
                            </span>
                            {festivo.tipo === 'regional' && (
                              <Badge variant="primary" size="sm" className="self-start">
                                {getRegionName(festivo.region_codigo)}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-auto">
                          <CustomSelect
                            value={config}
                            onChange={(e) => handleFestivoConfig(festivo.id, e.target.value)}
                            options={[
                              { id: 'horario_normal', value: 'horario_normal', label: 'Horario Normal' },
                              { id: 'horario_especial', value: 'horario_especial', label: 'Horario Especial' },
                              { id: 'cerrado', value: 'cerrado', label: 'Cerrado' }
                            ]}
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'fechas' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <h3 className="text-lg font-semibold">Fechas Especiales</h3>
            <button
              onClick={() => setShowFechaEspecialModal(true)}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium w-full sm:w-auto"
            >
              + Agregar Fecha Especial
            </button>
          </div>
          
          {fechasEspeciales.length === 0 ? (
            <div className="bg-gray-800 rounded-lg p-6 text-center text-gray-400">
              No hay fechas especiales configuradas
            </div>
            ) : (
            <>
              {/* Vista móvil: Cards en lista vertical */}
              <div className="block md:hidden space-y-3">
                {fechasEspeciales.map(fecha => (
                  <div key={fecha.id} className="bg-gray-800 rounded-lg border border-gray-700">
                    <div className="p-4">
                      {/* Título y badge */}
                      <div className="flex items-center mb-3">
                        <h4 className="text-lg font-semibold text-white">{fecha.nombre}</h4>
                        <Badge 
                          variant={
                            fecha.tipo === 'cierre_total' ? 'danger' :
                            fecha.tipo === 'horario_especial' ? 'warning' :
                            'info'
                          } 
                          size="sm" 
                          className="ml-2"
                        >
                          {fecha.tipo === 'cierre_total' ? 'Cerrado' :
                            fecha.tipo === 'horario_especial' ? 'Horario Especial' :
                            'Mantenimiento'}
                        </Badge>
                      </div>
                      
                      {/* Información de la fecha */}
                      <div className="text-sm">
                        <div className="flex items-center mb-2">
                          <FaCalendar className="mr-2 text-gray-500" />
                          <span className="text-gray-300">
                            {moment(fecha.fecha_inicio).format('DD/MM/YYYY')}
                            {fecha.fecha_fin && ` - ${moment(fecha.fecha_fin).format('DD/MM/YYYY')}`}
                          </span>
                        </div>
                        
                        {fecha.tipo === 'horario_especial' && fecha.horario_especial && (
                          <div className="flex items-center mb-2">
                            <FaClock className="mr-2 text-gray-500" />
                            <span className="text-gray-300">
                              {formatearHorarioEspecial(fecha.horario_especial)}
                            </span>
                          </div>
                        )}
                        
                        {fecha.motivo && (
                          <div className="flex items-center">
                            <FaFileAlt className="mr-2 text-gray-500" />
                            <span className="text-gray-300">{fecha.motivo}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Botones móviles */}
                    <div className="flex justify-between border-t border-gray-700 bg-gray-800">
                      <button
                        onClick={() => editFechaEspecial(fecha)}
                        className="flex-1 py-3 flex flex-col items-center justify-center text-yellow-500"
                        title="Editar"
                      >
                        <FaPen className="text-lg" />
                        <span className="text-xs mt-1">Editar</span>
                      </button>
                      
                      <button
                        onClick={() => deleteFechaEspecial(fecha.id)}
                        className="flex-1 py-3 flex flex-col items-center justify-center text-red-500"
                        title="Eliminar"
                      >
                        <FaTrash className="text-lg" />
                        <span className="text-xs mt-1">Eliminar</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Vista escritorio: Grid de 3 columnas */}
              <div className="hidden md:grid md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                {fechasEspeciales.map(fecha => (
                  <div key={fecha.id} className="bg-gray-800 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors flex flex-col">
                    {/* Header con título y botones */}
                    <div className="flex justify-between items-start p-4 pb-2">
                      <h4 className="font-medium text-white leading-tight pr-4">{fecha.nombre}</h4>
                      <div className="flex space-x-2 flex-shrink-0">
                        <button
                          onClick={() => editFechaEspecial(fecha)}
                          className="text-white bg-yellow-600 hover:bg-yellow-700 px-2 py-1 rounded transition-colors"
                          title="Editar fecha especial"
                        >
                          <FaPen className="text-sm" />
                        </button>
                        <button
                          onClick={() => deleteFechaEspecial(fecha.id)}
                          className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded transition-colors"
                          title="Eliminar fecha especial"
                        >
                          <FaTrash className="text-sm" />
                        </button>
                      </div>
                    </div>

                    {/* Contenido */}
                    <div className="px-4 pb-4">
                      <div className="space-y-2">
                        <div className="text-sm text-gray-400 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <FaCalendar className="text-xs flex-shrink-0" />
                            <span>
                              {moment(fecha.fecha_inicio).format('DD/MM/YY')}
                              {fecha.fecha_fin && ` - ${moment(fecha.fecha_fin).format('DD/MM/YY')}`}
                            </span>
                          </div>
                          <Badge 
                            variant={
                              fecha.tipo === 'cierre_total' ? 'danger' :
                              fecha.tipo === 'horario_especial' ? 'warning' :
                              'info'
                            } 
                            size="sm"
                          >
                            {fecha.tipo === 'cierre_total' ? 'Cerrado' :
                              fecha.tipo === 'horario_especial' ? 'Horario Especial' :
                              'Mantenimiento'}
                          </Badge>
                        </div>
                        
                        {fecha.tipo === 'horario_especial' && fecha.horario_especial && (
                          <div className="text-sm text-gray-400 flex items-center gap-2">
                            <FaClock className="text-xs flex-shrink-0" />
                            <span>{formatearHorarioEspecial(fecha.horario_especial)}</span>
                          </div>
                        )}
                        
                        {fecha.motivo && (
                          <div className="text-sm text-gray-500 flex items-center gap-2">
                            <FaFileAlt className="text-xs flex-shrink-0" />
                            <span className="truncate">{fecha.motivo}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'horarios' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <h3 className="text-lg font-semibold">Horarios Bloqueados</h3>
            <button
              onClick={() => setShowHorarioBloqueadoModal(true)}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium w-full sm:w-auto"
            >
              + Agregar Horario Bloqueado
            </button>
          </div>
          
          {horariosBloqueados.length === 0 ? (
            <div className="bg-gray-800 rounded-lg p-6 text-center text-gray-400">
              No hay horarios bloqueados configurados
            </div>
          ) : (
            <>
              {/* Vista móvil: Lista vertical */}
              <div className="block md:hidden space-y-3">
                {horariosBloqueados.map(horario => (
                  <div key={horario.id} className="bg-gray-800 rounded-lg border border-gray-700">
                    <div className="p-4">
                      {/* Título y badge */}
                      <div className="flex items-center mb-3">
                        <h4 className="text-lg font-semibold text-white">{horario.motivo}</h4>
                        <Badge variant="warning" size="sm" className="ml-2">
                          {horario.dia_semana === 'todos' ? 'Todos' : 
                            horario.dia_semana.charAt(0).toUpperCase() + horario.dia_semana.slice(1)}
                        </Badge>
                      </div>
                      
                      {/* Información del horario */}
                      <div className="text-sm">
                        <div className="flex items-center mb-2">
                          <FaClock className="mr-2 text-gray-500" />
                          <span className="text-gray-300">
                            {horario.hora_inicio} - {horario.hora_fin}
                          </span>
                        </div>
                        
                        <div className="flex items-center">
                          <FaCalendar className="mr-2 text-gray-500" />
                          <span className="text-gray-300">
                            Vigente desde {moment(horario.fecha_inicio_vigencia).format('DD/MM/YYYY')}
                            {horario.fecha_fin_vigencia && 
                              ` hasta ${moment(horario.fecha_fin_vigencia).format('DD/MM/YYYY')}`}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Botones móviles */}
                    <div className="flex justify-between border-t border-gray-700 bg-gray-800">
                      <button
                        onClick={() => editHorarioBloqueado(horario)}
                        className="flex-1 py-3 flex flex-col items-center justify-center text-yellow-500"
                        title="Editar"
                      >
                        <FaPen className="text-lg" />
                        <span className="text-xs mt-1">Editar</span>
                      </button>
                      
                      <button
                        onClick={() => deleteHorarioBloqueado(horario.id)}
                        className="flex-1 py-3 flex flex-col items-center justify-center text-red-500"
                        title="Eliminar"
                      >
                        <FaTrash className="text-lg" />
                        <span className="text-xs mt-1">Eliminar</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Vista escritorio: Grid de 3 columnas */}
              <div className="hidden md:grid md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                {horariosBloqueados.map(horario => (
                  <div key={horario.id} className="bg-gray-800 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors flex flex-col">
                    {/* Header con título y botones */}
                    <div className="flex justify-between items-start p-4 pb-2">
                      <h4 className="font-medium text-white leading-tight pr-4">{horario.motivo}</h4>
                      <div className="flex space-x-2 flex-shrink-0">
                        <button
                          onClick={() => editHorarioBloqueado(horario)}
                          className="text-white bg-yellow-600 hover:bg-yellow-700 px-2 py-1 rounded transition-colors"
                          title="Editar horario bloqueado"
                        >
                          <FaPen className="text-sm" />
                        </button>
                        <button
                          onClick={() => deleteHorarioBloqueado(horario.id)}
                          className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded transition-colors"
                          title="Eliminar horario bloqueado"
                        >
                          <FaTrash className="text-sm" />
                        </button>
                      </div>
                    </div>

                    {/* Contenido */}
                    <div className="px-4 pb-4">
                      <div className="space-y-2">
                        <div className="text-sm text-gray-400 flex items-center gap-2">
                          <FaClock className="text-xs flex-shrink-0" />
                          <span>{horario.hora_inicio} - {horario.hora_fin}</span>
                        </div>
                        
                        <div className="text-sm text-gray-500 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <FaCalendar className="text-xs flex-shrink-0" />
                            <span>
                              Desde {moment(horario.fecha_inicio_vigencia).format('DD/MM/YY')}
                              {horario.fecha_fin_vigencia && 
                                ` hasta ${moment(horario.fecha_fin_vigencia).format('DD/MM/YY')}`}
                            </span>
                          </div>
                          <Badge variant="warning" size="sm">
                            {horario.dia_semana === 'todos' ? 'Todos' : 
                              horario.dia_semana.charAt(0).toUpperCase() + horario.dia_semana.slice(1)}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Modal para horario especial de festivo */}
      <Modal
        isOpen={showHorarioEspecialModal}
        onClose={() => {
          setShowHorarioEspecialModal(false);
          setFestivoParaHorario(null);
          setHorarioEspecial(null);
        }}
        title={`Horario Especial - ${festivoParaHorario?.nombre}`}
      >
        <div className="p-4">
          <p className="text-gray-400 mb-4">
            Configura un horario especial para {festivoParaHorario?.nombre}
          </p>
          
          <HorarioEspecialDia
            defaultValue={horarioEspecial}
            onChange={setHorarioEspecial}
          />
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => {
                setShowHorarioEspecialModal(false);
                setFestivoParaHorario(null);
                setHorarioEspecial(null);
              }}
              className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              onClick={handleGuardarHorarioEspecial}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Guardar Horario
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal para fechas especiales */}
      <Modal
        isOpen={showFechaEspecialModal}
        onClose={() => {
          setShowFechaEspecialModal(false);
          resetFormFechaEspecial();
        }}
        title={editingFechaEspecial ? 'Editar Fecha Especial' : 'Nueva Fecha Especial'}
        size="2xl"
      >
        <form onSubmit={handleFechaEspecial} className="text-white">
          <div className="md:grid md:grid-cols-2 md:gap-4">
            {/* Columna izquierda: Información Básica */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4 md:mb-0 border border-gray-600">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <FaCalendar className="text-blue-400 mr-2" />
                Información Básica
              </h3>
              
              {/* Nombre del evento */}
              <div className="mb-4">
                <label className="flex items-center text-sm font-medium mb-2">
                  <FaCalendar className="text-gray-400 mr-2 text-xs" />
                  Nombre del Evento<span className="text-red-500 ml-1">*</span>
                </label>
                <ValidatedInput
                  type="text"
                  value={formFechaEspecial.nombre}
                  onChange={(e) => setFormFechaEspecial(prev => ({ ...prev, nombre: e.target.value }))}
                  className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  placeholder="Ej: Vacaciones de verano"
                  required
                  requiredMessage='Nombre del evento es requerido'
                />
              </div>

              {/* Tipo */}
              <div className="mb-4">
                <label className="flex items-center text-sm font-medium mb-2">
                  <FaTags className="text-gray-400 mr-2 text-xs" />
                  Tipo<span className="text-red-500 ml-1">*</span>
                </label>
                <CustomSelect
                  value={formFechaEspecial.tipo}
                  onChange={(e) => setFormFechaEspecial(prev => ({ ...prev, tipo: e.target.value }))}
                  options={[
                    { id: 'cierre_total', value: 'cierre_total', label: 'Cierre Total' },
                    { id: 'horario_especial', value: 'horario_especial', label: 'Horario Especial' },
                    { id: 'mantenimiento', value: 'mantenimiento', label: 'Mantenimiento' }
                  ]}
                  className="w-full"
                  required
                  requiredMessage='Tipo es requerido'
                />
              </div>

              {/* Motivo */}
              <div className="mb-4">
                <label className="flex items-center text-sm font-medium mb-2">
                  <FaFileAlt className="text-gray-400 mr-2 text-xs" />
                  Motivo
                </label>
                <textarea
                  value={formFechaEspecial.motivo}
                  onChange={(e) => setFormFechaEspecial(prev => ({ ...prev, motivo: e.target.value }))}
                  className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  placeholder="Describe el motivo..."
                  rows={3}
                />
              </div>
            </div>
            
            {/* Columna derecha: Fechas y Horarios */}
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <FaClock className="text-green-400 mr-2" />
                Fechas y Horarios
              </h3>
              
              {/* Fechas en una fila en desktop, columna en móvil */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="flex items-center text-sm font-medium mb-2">
                    <FaCalendar className="text-gray-400 mr-2 text-xs" />
                    Fecha Inicio<span className="text-red-500 ml-1">*</span>
                  </label>
                  <ValidatedInput
                    type="date"
                    value={formFechaEspecial.fecha_inicio}
                    onChange={(e) => setFormFechaEspecial(prev => ({ ...prev, fecha_inicio: e.target.value }))}
                    className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    required
                    requiredMessage='Fecha inicio es requerida'
                  />
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium mb-2">
                    <FaCalendar className="text-gray-400 mr-2 text-xs" />
                    Fecha Fin (opcional)
                  </label>
                  <input
                    type="date"
                    value={formFechaEspecial.fecha_fin}
                    onChange={(e) => setFormFechaEspecial(prev => ({ ...prev, fecha_fin: e.target.value }))}
                    className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  />
                </div>
              </div>

              {/* Horario Especial si aplica */}
              {formFechaEspecial.tipo === 'horario_especial' && (
                <div className="border-t border-gray-600 pt-4">
                  <label className="flex items-center text-sm font-medium mb-2">
                    <FaClock className="text-gray-400 mr-2 text-xs" />
                    Horario Especial
                  </label>
                  <HorarioEspecialDia
                    defaultValue={formFechaEspecial.horario_especial}
                    onChange={(horario) => setFormFechaEspecial(prev => ({ ...prev, horario_especial: horario }))}
                  />
                </div>
              )}

            </div>
          </div>
          
          {/* Separador visual y botones */}
          <div className="border-t border-gray-600 pt-4 mt-4">
            <div className="flex flex-col md:flex-row gap-3 md:justify-end">
              <Button 
                variant="secondary" 
                type="button" 
                onClick={() => {
                  setShowFechaEspecialModal(false);
                  resetFormFechaEspecial();
                }}
                className="w-full md:w-auto py-2.5 md:px-6 text-sm font-medium order-1"
              >
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                className="w-full md:w-auto py-2.5 md:px-6 text-sm font-medium flex items-center justify-center gap-2 order-2"
              >
                <FaSave />
                {editingFechaEspecial ? 'Actualizar' : 'Crear'} Fecha Especial
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Modal para horarios bloqueados */}
      <Modal
        isOpen={showHorarioBloqueadoModal}
        onClose={() => {
          setShowHorarioBloqueadoModal(false);
          resetFormHorarioBloqueado();
        }}
        title={editingHorarioBloqueado ? 'Editar Horario Bloqueado' : 'Nuevo Horario Bloqueado'}
        size="2xl"
      >
        <form onSubmit={handleHorarioBloqueado} className="text-white">
          <div className="md:grid md:grid-cols-2 md:gap-6">
            {/* Columna izquierda: Información Básica */}
            <div className="bg-gray-800 p-4 rounded-lg mb-6 md:mb-0 border border-gray-600">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <FaClock className="text-orange-400 mr-2" />
                Información Básica
              </h3>
              
              {/* Motivo */}
              <div className="mb-4">
                <label className="flex items-center text-sm font-medium mb-2">
                  <FaFileAlt className="text-gray-400 mr-2 text-xs" />
                  Motivo<span className="text-red-500 ml-1">*</span>
                </label>
                <ValidatedInput
                  type="text"
                  value={formHorarioBloqueado.motivo}
                  onChange={(e) => setFormHorarioBloqueado(prev => ({ ...prev, motivo: e.target.value }))}
                  className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  placeholder="Ej: Reunión semanal de equipo"
                  required
                  requiredMessage='Motivo es requerido'
                />
              </div>

              {/* Día de la semana */}
              <div className="mb-4">
                <label className="flex items-center text-sm font-medium mb-2">
                  <FaCalendar className="text-gray-400 mr-2 text-xs" />
                  Día de la Semana
                </label>
                <CustomSelect
                  value={formHorarioBloqueado.dia_semana}
                  onChange={(e) => setFormHorarioBloqueado(prev => ({ ...prev, dia_semana: e.target.value }))}
                  options={[
                    { id: 'todos', value: 'todos', label: 'Todos los días' },
                    { id: 'lunes', value: 'lunes', label: 'Lunes' },
                    { id: 'martes', value: 'martes', label: 'Martes' },
                    { id: 'miercoles', value: 'miercoles', label: 'Miércoles' },
                    { id: 'jueves', value: 'jueves', label: 'Jueves' },
                    { id: 'viernes', value: 'viernes', label: 'Viernes' },
                    { id: 'sabado', value: 'sabado', label: 'Sábado' },
                    { id: 'domingo', value: 'domingo', label: 'Domingo' }
                  ]}
                  className="w-full"
                />
              </div>
            </div>
            
            {/* Columna derecha: Horarios y Vigencia */}
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <FaClock className="text-yellow-400 mr-2" />
                Horarios y Vigencia
              </h3>
              
              {/* Horarios */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="flex items-center text-sm font-medium mb-2">
                    <FaClock className="text-gray-400 mr-2 text-xs" />
                    Hora Inicio<span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="time"
                    value={formHorarioBloqueado.hora_inicio}
                    onChange={(e) => setFormHorarioBloqueado(prev => ({ ...prev, hora_inicio: e.target.value }))}
                    className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium mb-2">
                    <FaClock className="text-gray-400 mr-2 text-xs" />
                    Hora Fin<span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="time"
                    value={formHorarioBloqueado.hora_fin}
                    onChange={(e) => setFormHorarioBloqueado(prev => ({ ...prev, hora_fin: e.target.value }))}
                    className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    required
                  />
                </div>
              </div>

              {/* Vigencia */}
              <div className="border-t border-gray-600 pt-4">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="flex items-center text-sm font-medium mb-2">
                      <FaCalendar className="text-gray-400 mr-2 text-xs" />
                      Vigente desde<span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="date"
                      value={formHorarioBloqueado.fecha_inicio_vigencia}
                      onChange={(e) => setFormHorarioBloqueado(prev => ({ ...prev, fecha_inicio_vigencia: e.target.value }))}
                      className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label className="flex items-center text-sm font-medium mb-2">
                      <FaCalendar className="text-gray-400 mr-2 text-xs" />
                      Vigente hasta (opcional)
                    </label>
                    <input
                      type="date"
                      value={formHorarioBloqueado.fecha_fin_vigencia}
                      onChange={(e) => setFormHorarioBloqueado(prev => ({ ...prev, fecha_fin_vigencia: e.target.value }))}
                      className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Separador visual y botones */}
          <div className="border-t border-gray-600 pt-6 mt-6">
            <div className="flex flex-col md:flex-row gap-3 md:justify-end">
              <Button 
                variant="secondary" 
                type="button" 
                onClick={() => {
                  setShowHorarioBloqueadoModal(false);
                  resetFormHorarioBloqueado();
                }}
                className="w-full md:w-auto py-2.5 md:px-6 text-sm font-medium order-1"
              >
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                className="w-full md:w-auto py-2.5 md:px-6 text-sm font-medium flex items-center justify-center gap-2 order-2"
              >
                <FaSave />
                {editingHorarioBloqueado ? 'Actualizar' : 'Crear'} Horario Bloqueado
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default GestionFestivos;